import { useMemoize } from "@vueuse/core";
import { debounce } from "lodash";
import { defineStore } from "pinia";
import { breadthFirstSearch } from "src/shared/graph-v2/breadthFirstSearch";
import { getLeafNodes } from "src/shared/graph-v2/getLeafNodes";
import { transposeAdjacencyList } from "src/shared/graph-v2/transposeAdjacencyList";
import { reactive, ref } from "vue";
import { useBuiltComponentStore } from "../component-built";
import { useComponentVariableSearchableStore } from "../component-variable-searchable";
import { incrementGlobalComponentGraphVersion } from "../component-graph/global-component-graph-version";
const getComponentVariableUniqueId = (BuiltComponentVariable) => {
    return `${BuiltComponentVariable.context.componentId}-${BuiltComponentVariable.name}`;
};
const calculateComponentVariableNodeType = (parentComponentIds, childComponentIds) => {
    if (parentComponentIds.length > 0 && childComponentIds.length > 0) {
        return "MEDIATOR"; // Has both parents and children
    }
    if (parentComponentIds.length > 0) {
        return "INSTANCE"; // Has a parent but no children
    }
    if (childComponentIds.length > 0) {
        return "ASSET"; // Has children but no parent
    }
    return "INDEPENDENT"; // No parents or children
};
export const hasParentComponentVariable = (nodeType) => {
    return nodeType === "MEDIATOR" || nodeType === "INSTANCE";
};
export const hasChildComponentVariables = (nodeType) => {
    return nodeType === "MEDIATOR" || nodeType === "ASSET";
};
export const useComponentGraphStoreV2 = defineStore("componentGraphStoreV2", () => {
    const componentVariableSearchableStore = useComponentVariableSearchableStore();
    const builtComponentStore = useBuiltComponentStore();
    const parentDirectedComponentGraph = ref({});
    const childDirectedComponentGraph = ref({});
    const componentVariableNodeContexts = reactive({});
    const recalculateChildDirectedComponentGraph = () => {
        // Transpose the parent graph to get the child graph
        childDirectedComponentGraph.value = transposeAdjacencyList(parentDirectedComponentGraph.value);
    };
    const getParentComponentIdsUsingVariable = (variable) => {
        const parentComponentIdsThatHaveThisVariable = [];
        const parentComponentIds = getParentComponentIds(variable.context.componentId);
        for (const parentComponentId of parentComponentIds) {
            const parentComponent = builtComponentStore.getBuiltComponent(parentComponentId);
            if (parentComponent === null || parentComponent === void 0 ? void 0 : parentComponent.findVariable(variable.name)) {
                parentComponentIdsThatHaveThisVariable.push(parentComponentId);
            }
        }
        return parentComponentIdsThatHaveThisVariable;
    };
    const getChildComponentIdsUsingVariable = (variable) => {
        const childComponentIdsThatHaveThisVariable = [];
        const childComponentIds = getChildComponentIds(variable.context.componentId);
        for (const childComponentId of childComponentIds) {
            const childComponent = builtComponentStore.getBuiltComponent(childComponentId);
            if (childComponent === null || childComponent === void 0 ? void 0 : childComponent.findVariable(variable.name)) {
                childComponentIdsThatHaveThisVariable.push(childComponentId);
            }
        }
        return childComponentIdsThatHaveThisVariable;
    };
    const recalculateComponentVariableNodeContexts = () => {
        for (const componentId in parentDirectedComponentGraph.value) {
            const builtComponent = builtComponentStore.getBuiltComponent(componentId);
            if (!builtComponent) {
                continue;
            }
            for (const variable of builtComponent.listVariables()) {
                const variableId = getComponentVariableUniqueId(variable);
                const parentComponentIdsThatHaveThisVariable = getParentComponentIdsUsingVariable(variable);
                const childComponentIdsThatHaveThisVariable = getChildComponentIdsUsingVariable(variable);
                componentVariableNodeContexts[variableId] = {
                    immediateParentComponentId: parentComponentIdsThatHaveThisVariable[0],
                    parentComponentIds: parentComponentIdsThatHaveThisVariable,
                    childComponentIds: childComponentIdsThatHaveThisVariable,
                    type: calculateComponentVariableNodeType(parentComponentIdsThatHaveThisVariable, childComponentIdsThatHaveThisVariable),
                };
            }
            componentVariableSearchableStore.recalculateSearchables(componentId);
        }
    };
    const getChildComponentIds = useMemoize((componentId, degree = null) => {
        const visited = [];
        breadthFirstSearch(childDirectedComponentGraph.value, componentId, degree, (node) => {
            visited.push(node);
        });
        return visited.filter((id) => id !== componentId);
    });
    const getParentComponentIds = useMemoize((componentId) => {
        const visited = [];
        breadthFirstSearch(parentDirectedComponentGraph.value, componentId, null, (node) => {
            visited.push(node);
        });
        return visited.filter((id) => id !== componentId);
    });
    const getRootComponentIds = useMemoize(() => {
        return getLeafNodes(parentDirectedComponentGraph.value);
    });
    const getChildTreeRecurse = (componentId, visited = {}) => {
        visited[componentId] = true;
        const root = {
            componentId,
            children: [],
        };
        const childComponentIds = getChildComponentIds(componentId, 1);
        childComponentIds.forEach((childComponentId) => {
            if (visited[childComponentId]) {
                return;
            }
            const childTree = getChildTreeRecurse(childComponentId, visited);
            root.children.push(childTree);
        });
        return root;
    };
    const getComponentTree = useMemoize((componentId) => {
        return getChildTreeRecurse(componentId, {});
    });
    const getCompleteComponentTree = useMemoize((componentId) => {
        // Find the root ancestor by traversing up the parent chain
        let currentId = componentId;
        let rootId = componentId;
        const parentChain = [componentId];
        let parentIds = parentDirectedComponentGraph.value[currentId];
        while (parentIds && parentIds.length > 0) {
            currentId = parentIds[0]; // Follow the first parent
            parentChain.unshift(currentId);
            parentIds = parentDirectedComponentGraph.value[currentId];
        }
        rootId = currentId;
        // Build the tree from root down to the target component
        const buildTreeWithPath = (nodeId, pathIndex, visited = {}) => {
            visited[nodeId] = true;
            const node = {
                componentId: nodeId,
                children: [],
            };
            // If this is part of the path to the target component
            if (pathIndex < parentChain.length &&
                nodeId === parentChain[pathIndex]) {
                if (pathIndex === parentChain.length - 1) {
                    // This is the target component, include all its children
                    const childComponentIds = getChildComponentIds(nodeId, 1);
                    childComponentIds.forEach((childId) => {
                        if (!visited[childId]) {
                            const childTree = getChildTreeRecurse(childId, visited);
                            node.children.push(childTree);
                        }
                    });
                }
                else {
                    // This is an ancestor of the target, continue down the path
                    const nextInPath = parentChain[pathIndex + 1];
                    const childTree = buildTreeWithPath(nextInPath, pathIndex + 1, visited);
                    node.children.push(childTree);
                }
            }
            return node;
        };
        return buildTreeWithPath(rootId, 0);
    });
    const componentGraphHasBeenModifiedRecalculateEverything = debounce(() => {
        recalculateChildDirectedComponentGraph();
        recalculateComponentVariableNodeContexts();
        getChildComponentIds.cache.clear();
        getParentComponentIds.cache.clear();
        getRootComponentIds.cache.clear();
        getComponentTree.cache.clear();
        getCompleteComponentTree.cache.clear();
        incrementGlobalComponentGraphVersion();
    }, 100);
    const updateComponentGraphNode = (component) => {
        parentDirectedComponentGraph.value[component.id] =
            component.component_ids;
        componentGraphHasBeenModifiedRecalculateEverything();
    };
    const getComponentVariableNodeContext = (componentId, variableName) => {
        const variableId = `${componentId}-${variableName}`;
        return componentVariableNodeContexts[variableId];
    };
    return {
        updateComponentGraphNode,
        parentDirectedComponentGraph,
        getChildComponentIds,
        getParentComponentIds,
        getRootComponentIds,
        getComponentTree,
        getCompleteComponentTree,
        getComponentVariableNodeContext,
    };
});
